import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import React from 'react';

import { useMembershipPlanPricing } from '../hooks/useMembershipPlanPricing';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { getProduct } from '../subscriptions/catalog';
import { normalizeEntitlementsMap, ownsProduct } from '../subscriptions/entitlements';
import { canShowFreeTrialChoice, isMembershipTrialing } from '../subscriptions/membershipPurchaseUi';
import { resolveCheckoutReturnTo } from '../subscriptions/purchaseSteps';
import type { EntitlementsMap, MembershipPlanId, MembershipState } from '../subscriptions/types';
import { isIosNativeApp } from '../utils/nativeCheckoutBridge';

export interface MembershipPlanCardProps {
  planId: MembershipPlanId;
  assignmentId?: number;
  entitlements?: EntitlementsMap | null;
  membership?: MembershipState | null;
  returnTo?: string;
  disabled?: boolean;
  /** Reason the plan cannot be bought right now, shown instead of the button. */
  disabledReason?: string;
  /** Highlight as the better-value plan. */
  emphasized?: boolean;
  /** Ribbon text, e.g. "Best value". */
  badge?: string;
}

const MembershipPlanCard: React.FC<MembershipPlanCardProps> = ({
  planId,
  assignmentId,
  entitlements,
  membership = null,
  returnTo,
  disabled = false,
  disabledReason,
  emphasized = false,
  badge,
}) => {
  const product = getProduct(planId);
  const pricing = useMembershipPlanPricing()[planId];
  const { loading, error, clearError, startCheckout, openBillingPortal } = useSubscriptionCheckout();
  const normalized = normalizeEntitlementsMap(entitlements);
  const owned = ownsProduct(normalized, planId);
  const trialing = isMembershipTrialing(membership);
  const trialChoice = canShowFreeTrialChoice(membership, pricing.trialLabel);
  const native = isIosNativeApp();

  if (!product) {
    return null;
  }

  const canPurchase = !disabled && !loading;

  const checkoutReturnTo = () => {
    const resolvedReturnTo = resolveCheckoutReturnTo({
      entitlements: normalized,
      productId: planId,
      assignmentId,
    });
    return resolvedReturnTo ?? returnTo;
  };

  const runCheckout = async (skipTrial: boolean) => {
    clearError();
    const effectiveReturnTo = checkoutReturnTo();
    await startCheckout({
      ...(assignmentId !== undefined ? { assignmentId } : {}),
      productId: planId,
      skipTrial,
      ...(effectiveReturnTo ? { returnTo: effectiveReturnTo } : {}),
    });
  };

  const runUpgradeFromTrial = async () => {
    clearError();
    if (assignmentId === undefined || native || membership?.provider === 'apple_iap') {
      return;
    }
    const url = await openBillingPortal(assignmentId);
    if (url) {
      window.location.assign(url);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        height: '100%',
        borderRadius: 2,
        border: emphasized ? 2 : 1,
        borderColor: emphasized ? 'secondary.main' : 'divider',
        bgcolor: emphasized ? 'action.hover' : 'background.paper',
      }}
      data-testid={`membership-plan--${planId}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {product.name}
        </Typography>
        {badge && <Chip label={badge} size="small" color="secondary" />}
        {owned && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Current plan"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      <Typography
        variant="h5"
        color="secondary.main"
        sx={{ fontWeight: 700 }}
        data-testid={`membership-plan-price--${planId}`}
      >
        {pricing.price}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {product.description}
      </Typography>

      {error && (
        <Alert
          severity="error"
          action={
            error.retryable ? (
              <Button color="inherit" size="small" onClick={() => void runCheckout(false)} disabled={loading}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">{error.message}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {error.instruction}
          </Typography>
        </Alert>
      )}

      <Box sx={{ mt: 'auto', pt: 1 }}>
        {disabled && disabledReason ? (
          <Alert severity="info" data-testid={`membership-plan-blocked--${planId}`}>
            <Typography variant="body2">{disabledReason}</Typography>
          </Alert>
        ) : trialing ? (
          <>
            {native || membership?.provider === 'apple_iap' ? (
              <Typography variant="body2" color="text.secondary">
                You are on a free trial. Manage billing in Apple Account settings to start paid
                membership early.
              </Typography>
            ) : (
              <>
                <Button
                  fullWidth
                  variant={emphasized ? 'contained' : 'outlined'}
                  color="secondary"
                  disabled={!canPurchase || assignmentId === undefined}
                  onClick={() => void runUpgradeFromTrial()}
                  data-attr={`membership-plan_subscribe-now--${planId}`}
                  data-testid={`membership-plan-subscribe-now--${planId}`}
                >
                  {loading ? 'Opening…' : 'Subscribe now'}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Opens billing management to unlock the full program immediately.
                </Typography>
              </>
            )}
          </>
        ) : trialChoice ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              fullWidth
              variant={emphasized ? 'contained' : 'outlined'}
              color="secondary"
              disabled={!canPurchase}
              onClick={() => void runCheckout(false)}
              data-attr={`membership-plan_start-trial--${planId}`}
              data-testid={`membership-plan-start-trial--${planId}`}
            >
              {loading ? 'Starting…' : 'Start free trial'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              disabled={!canPurchase}
              onClick={() => void runCheckout(true)}
              data-attr={`membership-plan_subscribe-now--${planId}`}
            >
              {loading ? 'Starting…' : 'Subscribe now'}
            </Button>
          </Stack>
        ) : (
          <Button
            fullWidth
            variant={emphasized ? 'contained' : 'outlined'}
            color="secondary"
            disabled={!canPurchase}
            onClick={() => void runCheckout(true)}
            data-attr={`membership-plan_subscribe--${planId}`}
            data-testid={`membership-plan-subscribe--${planId}`}
          >
            {loading ? 'Starting…' : 'Subscribe'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MembershipPlanCard;
